// ---------------------------------------------------------------------------
// Bootstrap: finds all ribbon canvases, reads the configuration from the
// data attributes and connects the base with the chosen interaction driver.
//
// Three.js and Motion are loaded via dynamic import only when a ribbon canvas
// is present – pages without a ribbon stay lean.
//
// Markup example (configured by the developer at embed time):
//   <canvas class="ribbon-canvas" data-ribbon
//           data-ribbon-mode="reveal"
//           data-ribbon-twists="1.25"
//           data-ribbon-bend="rising-left-to-right"
//           data-ribbon-growth-anchor="left"></canvas>
// ---------------------------------------------------------------------------
function parseConfig(el) {
    const d = el.dataset;
    const config = {};

    if (d.ribbonTwists !== undefined) config.twistTurns = parseFloat(d.ribbonTwists);
    if (d.ribbonWidth !== undefined) config.width = parseFloat(d.ribbonWidth);
    if (d.ribbonSegments !== undefined) config.segments = parseInt(d.ribbonSegments, 10);
    if (d.ribbonTaper !== undefined) config.taper = parseFloat(d.ribbonTaper);
    if (d.ribbonBend !== undefined) config.bend = d.ribbonBend;
    if (d.ribbonColor !== undefined) config.color = parseColor(d.ribbonColor);
    if (d.ribbonGrowthAnchor !== undefined) config.growthAnchor = d.ribbonGrowthAnchor;
    if (d.ribbonMouse !== undefined) config.mouseParallax = d.ribbonMouse !== 'false';

    return config;
}

function parseDriverOptions(el) {
    const d = el.dataset;
    const opts = {};

    if (d.ribbonDuration !== undefined) opts.duration = parseFloat(d.ribbonDuration);
    // reveal driver: height of the bottom band that must be fully visible before
    // the ribbon builds. A bare number is treated as px; any other value is used
    // verbatim as a CSS length (e.g. "12vh").
    if (d.ribbonRevealBuffer !== undefined) {
        const raw = d.ribbonRevealBuffer;
        opts.buffer = /^\d+(\.\d+)?$/.test(raw.trim()) ? parseFloat(raw) : raw;
    }
    // autoplay driver: inView visibility ratio ("all"/"some" or 0..1).
    if (d.ribbonAmount !== undefined) opts.amount = parseAmount(d.ribbonAmount);

    return opts;
}

function parseAmount(value) {
    // Accepts the inView keywords "all"/"some" or a numeric ratio (0..1).
    if (value === 'all' || value === 'some') return value;
    return parseFloat(value);
}

function parseColor(value) {
    // Accepts "#ee0000", "ee0000" or "0xee0000".
    const hex = value.replace(/^#/, '').replace(/^0x/i, '');
    return parseInt(hex, 16);
}

async function initRibbons() {
    const canvases = document.querySelectorAll('canvas[data-ribbon]');
    if (!canvases.length) return;

    // Load the heavyweight modules (Three.js, Motion) only now.
    const [{ Ribbon }, { DRIVERS }] = await Promise.all([
        import('./ribbon/Ribbon.js'),
        import('./ribbon/drivers.js'),
    ]);

    canvases.forEach((canvas) => {
        const mode = canvas.dataset.ribbonMode || 'reveal';
        const driver = DRIVERS[mode];
        if (!driver) {
            console.warn(`[ribbon] unknown mode "${mode}"`);
            return;
        }

        // Target for the scroll/visibility calculation: closest section.
        const target = canvas.closest('section') || canvas.parentElement || canvas;

        const ribbon = new Ribbon(canvas, parseConfig(canvas));
        driver(ribbon, target, parseDriverOptions(canvas));
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRibbons);
} else {
    initRibbons();
}
